import { fetchWithNullHandling } from "@/lib/fetch";
import { SidebarMenuItem, sidebarMenuItemsPermissionRequired } from "@/types/navigation";
import { IOrgPermissionsAndRole, PermissionName, Role } from "@/types/permission";
import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

export interface UserState {
  signedIn: boolean;
  secToken?: string;
  userId?: string;
  userEmail?: string;
  permissions: PermissionName[];
  role: Role,
}

export const useUserStore = defineStore("userStore", () => {
  const state = reactive<UserState>({
    signedIn: false,
    secToken: "",
    userId: undefined,
    userEmail: undefined,
    permissions: [],
    role: Role.Teacher
  });

  const isSystemAdmin = computed(() => state.role === Role.SystemAdmin);

  function setUserSession(inId: string, inEmail: string) {
    state.userId = inId;
    state.userEmail = inEmail;
    state.signedIn = true;
  }

  function setPermissionsAndRole(inPermissionsAndRole?: IOrgPermissionsAndRole) {
    if(!inPermissionsAndRole) {
      state.permissions = [];
      state.role = Role.Teacher;
      return;
    }

    state.permissions = inPermissionsAndRole.permissions.map(p => p.name);
    state.role = inPermissionsAndRole.role?.name as Role;
  }

  function clearUserSession() {
    state.userId = undefined;
    state.userEmail = undefined;
    state.signedIn = false;
    state.role = Role.Teacher;
    state.permissions = [];
  }

  function hasPermission(inPermission: PermissionName | PermissionName[]): boolean {
    if(isSystemAdmin.value) {
      return true;
    }
    const requiredPermissions = Array.isArray(inPermission) ? inPermission : [inPermission];
    return requiredPermissions.every(permission => state.permissions.includes(permission));
  }

  function hasRole(inRole: Role): boolean {
    if(isSystemAdmin.value) {
      return true;
    }

    return state.role === inRole;
  }

  function isUiEnabled(uiElement: SidebarMenuItem) {
    if(isSystemAdmin.value) {
      return true;
    }

    const requiredPermissions = sidebarMenuItemsPermissionRequired[uiElement];
    if(requiredPermissions.length === 0) {
      return true;
    }
    return requiredPermissions.some(permission => state.permissions.includes(permission));
  }

  async function signOut() {
    try {
      const r = await fetchWithNullHandling("/api/auth/logout", {
        method: "post"
      });
      if(r.ok) {
        clearUserSession();
        return true;
      }
      else {
        return false;
      }
    }
    catch(err) {
      console.error("Failed to sign user out", err);
      return false;
    }
  }

  return {
    ...toRefs(state),
    isSystemAdmin,
    setUserSession,
    setPermissionsAndRole,
    clearUserSession,
    hasPermission,
    isUiEnabled,
    signOut,
    hasRole
  };
}, {
  persist: true
});
